import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { reaction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { ErrorBoundary } from 'bernie-view';
import { isVrbo } from 'src/common/stores/context-store-utils';
import { setCompressionBannerDismiss } from 'src/common/utils/compression-banner-utils';
import { Experiment, ExperimentControl, ExperimentVariant, useExperiment } from '@shared-ui/experiment-context';
import { useLocalization } from '@shared-ui/localization-context';
import { useRefContext } from '@shared-ui/ref-context';
import { SortAndFiltersParamsKeys, SortAndFiltersRangeParamKey } from '@shared-ui/retail-sort-and-filter-lodging';
import { SearchFormWrapping } from './search-form-wrapping';
import { isAlternateDestinationFeatureGate, isMetaChannel, isMetaChannelDefaultDatesSearch, isSEMChannelDefaultDatesSearch, updateResultsWithNewCriteria } from './universal-search-form-utils';
/* eslint-disable complexity */
const UniversalSearchFormWrapper = inject('analytics', 'searchResults', 'experiment', 'uiPage', 'context', 'analyticsSearch')(observer(({
  analytics,
  searchResults,
  experiment,
  uiPage,
  context,
  analyticsSearch
}) => {
  var _a, _b;
  const location = useLocation();
  const history = useHistory();
  const l10n = useLocalization();
  const [datePickerOpen, setDatePickerOpen] = React.useState(false);
  const universalSortFilterKeys = [...Object.values(SortAndFiltersParamsKeys), ...Object.values(SortAndFiltersRangeParamKey)];
  const searchQueryParams = new URLSearchParams(location.search);
  const queryParamStartDate = searchQueryParams.get('startDate') || '';
  const queryParamEndDate = searchQueryParams.get('endDate') || '';
  const queryParamFlexibleSearchRange = searchQueryParams.get('searchRange') || '';
  const queryParamLatLong = searchQueryParams.get('latLong') || '';
  const queryParamRegionId = searchQueryParams.get('regionId') || '';
  const queryParamDaysInFuture = searchQueryParams.get('daysInFuture') || '';
  const queryParamStayLength = searchQueryParams.get('stayLength') || '';
  const isMobile = Boolean(((_a = context === null || context === void 0 ? void 0 : context.deviceInformation) === null || _a === void 0 ? void 0 : _a.mobile) && !((_b = context === null || context === void 0 ? void 0 : context.deviceInformation) === null || _b === void 0 ? void 0 : _b.tablet));
  const isMetaOrSEMChannelDefaultDatesSearch = Boolean(isMetaChannelDefaultDatesSearch(searchQueryParams) || isSEMChannelDefaultDatesSearch(searchQueryParams));
  const isMetaOrSEMChannel = Boolean(isMetaChannel(searchQueryParams) || isSEMChannelDefaultDatesSearch(searchQueryParams));
  const openPlayBack = Boolean(searchQueryParams.get('openPlayBack') === 'true');
  const {
    exposure: openPlayBackDLPExposure
  } = useExperiment('Expand_playback_component_on_mWeb_SRP_for_users_coming_from_DLPs', openPlayBack && isMobile);
  const {
    exposure: autoOpenPlaybackVrboDatelessExposure,
    logExperiment: logAutoOpenPlaybackVrboDatelessExposure
  } = useExperiment('Web_Auto_open_Playback_for_Vrbo_Dateless_Searches');
  const isDlpOpenPlayBackEnabled = Boolean((openPlayBackDLPExposure === null || openPlayBackDLPExposure === void 0 ? void 0 : openPlayBackDLPExposure.bucket) === 1 && openPlayBack && isMobile);
  const autoOpenPlaybackDatelessSearch = (() => {
    if (isMobile && !queryParamStartDate && !queryParamEndDate && !queryParamFlexibleSearchRange) {
      logAutoOpenPlaybackVrboDatelessExposure(autoOpenPlaybackVrboDatelessExposure);
      return autoOpenPlaybackVrboDatelessExposure.bucket === 1;
    }
    return false;
  })();
  const isAlternateDestinationEnabled = Boolean(experiment && context && isAlternateDestinationFeatureGate(experiment, context));
  const {
    exposure: vrboFilterPersistenceOnDestinationChangeExposure,
    logExperiment: logVrboFilterPersistenceOnDestinationChangeExposure
  } = useExperiment('Vrbo_filter_persistence_on_destination_change');
  const {
    getTargets
  } = useRefContext();
  const {
    SearchForm: searchFormRef
  } = getTargets(['SearchForm']);
  // Auto open calendar when user lands on SRP from META/SEM channel and had not entered dates
  React.useEffect(() => {
    if (isMetaOrSEMChannelDefaultDatesSearch && !isMobile) {
      setDatePickerOpen(true);
    }
  }, []);
  // Auto open calender for dateless SRP for vrbo
  React.useEffect(() => {
    if (isVrbo(context) && !isMobile) {
      // It counts as dateless SRP if there are no dates and no flexible criteria (e.g. search range)
      if (!queryParamStartDate && !queryParamEndDate && !queryParamFlexibleSearchRange) {
        setDatePickerOpen(true);
      }
    }
  }, []);
  React.useEffect(() => {
    const uiPageOpenDatePickerDisposer = reaction(() => uiPage === null || uiPage === void 0 ? void 0 : uiPage.openNewDatePickerEvent, openNewDatePickerEvent => {
      var _a;
      if (openNewDatePickerEvent) {
        setDatePickerOpen(true);
        (_a = searchFormRef === null || searchFormRef === void 0 ? void 0 : searchFormRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
          block: 'start',
          behavior: 'instant'
        });
      }
    });
    return () => uiPageOpenDatePickerDisposer();
  }, [uiPage === null || uiPage === void 0 ? void 0 : uiPage.openNewDatePickerEvent]);
  const checkForSelectedFilters = () => {
    for (const key of searchQueryParams.keys()) {
      if (universalSortFilterKeys.includes(key) && searchQueryParams.get(key) !== 'RECOMMENDED') {
        return true;
      }
    }
    return false;
  };
  const handleSubmit = (event, inputs) => {
    var _a, _b, _c;
    event.preventDefault();
    analytics === null || analytics === void 0 ? void 0 : analytics.resetAnalyticsForNewPage();
    setCompressionBannerDismiss(false);
    const hasChangedDestination = ((_c = (_b = (_a = inputs === null || inputs === void 0 ? void 0 : inputs.propertySearchCriteriaInput) === null || _a === void 0 ? void 0 : _a.primary) === null || _b === void 0 ? void 0 : _b.destination) === null || _c === void 0 ? void 0 : _c.regionId) !== queryParamRegionId;
    const hasSelectedFilters = checkForSelectedFilters();
    if (searchResults && context && experiment && inputs && analyticsSearch) {
      if (vrboFilterPersistenceOnDestinationChangeExposure.bucket === 1 && hasChangedDestination && hasSelectedFilters) {
        logVrboFilterPersistenceOnDestinationChangeExposure(vrboFilterPersistenceOnDestinationChangeExposure);
      }
      updateResultsWithNewCriteria(inputs, location, history, searchResults, context, experiment, l10n, analyticsSearch, analytics);
    }
  };
  const searchFormProps = {
    dateLocalFormat: context === null || context === void 0 ? void 0 : context.localDateFormat,
    queryParamEndDate,
    queryParamStartDate,
    handleSubmit: handleSubmit,
    showDatePicker: datePickerOpen,
    toggleDatePicker: setDatePickerOpen,
    autoOpenPlayback: isMetaOrSEMChannel && isMobile || isDlpOpenPlayBackEnabled || autoOpenPlaybackDatelessSearch,
    includePlayback: true,
    dateFieldInitialFocus: false,
    queryParamLatLong: queryParamLatLong,
    alternateDestination: isAlternateDestinationEnabled,
    redirectToSearchResultOnSubmit: false,
    autoSubmitOnFieldChange: true,
    queryParamDaysInFuture,
    queryParamStayLength,
    queryRooms: []
  };
  return React.createElement(ErrorBoundary, {
    label: "hotel.search.universal-search-form"
  }, isMetaOrSEMChannel && isMobile ? React.createElement(Experiment, {
    name: "Highlight_dates_field_for_SEM_and_META_users_landing_on_mWeb_SRP",
    logExposure: true
  }, React.createElement(ExperimentControl, null, React.createElement(SearchFormWrapping, Object.assign({}, searchFormProps))), React.createElement(ExperimentVariant, {
    bucket: 1
  }, React.createElement(SearchFormWrapping, Object.assign({}, searchFormProps, {
    dateFieldInitialFocus: true
  })))) : React.createElement(SearchFormWrapping, Object.assign({}, searchFormProps)));
}));
export const SharedUiUniversalSearchFormWrapper = UniversalSearchFormWrapper;
SharedUiUniversalSearchFormWrapper.displayName = 'SharedUiUniversalSearchFormWrapper';